<script setup lang="ts">
import { useNow } from "@vueuse/core";
import { useTranslation } from "i18next-vue";
import QrcodeVue from "qrcode.vue";
import { computed, onMounted, ref, watch } from "vue";
import QRCheckTutor from "#/assets/icons/check.svg";
import QrScanner from "#/assets/icons/qr_scanner.svg";
import QrPhone from "#/assets/iconsDynamic/qr_phone.vue";
import QRScannerTutor from "#/assets/iconsDynamic/qr_scanner_tutor.vue";
import { useMainStore } from "#/store/main";
import { useThemeStore } from "#/store/theme";
import ButtonDefault from "#/ui/buttons/default/ButtonDefault.vue";
import AnimNum from "#/ui/recountNumber/RecountNumber.vue";

const props = defineProps<{ price: number; link: string }>();
const emit = defineEmits<(e: "cancel") => void>();

const themeStore = useThemeStore();
const storeMain = useMainStore();

const { t } = useTranslation();

const imageSettings = {
	src: themeStore.data?.logo || "",
	width: 60,
	height: 60,
	excavate: true,
};

let hasBeenReset = false;

const expiresAt = ref(0);
const timeout = computed(() => themeStore.data?.kiosk_qr_payment_timeout || 60);
const { now, pause } = useNow({ interval: 1000, controls: true });
const ttlPercent = computed(() => (timeLeft.value / timeout.value) * 100);

const timeLeft = computed(() => {
	const leftSeconds = Math.ceil((expiresAt.value - now.value.getTime()) / 1000);
	return Math.max(leftSeconds, 0);
});

const formattedTime = computed(() => {
	const minutes = Math.floor(timeLeft.value / 60);
	const seconds = timeLeft.value % 60;
	return `${minutes}:${seconds.toString().padStart(2, "0")}`;
});

const resetOrder = () => {
	emit("cancel");
};

onMounted(() => {
	expiresAt.value = now.value.getTime() + timeout.value * 1000;
});

watch(timeLeft, (value) => {
	if (value > 0 || hasBeenReset) return;

	hasBeenReset = true;
	pause();
	resetOrder();
});
</script>

<template>
	<div class="wrapper_qr_page">
		<video v-if="themeStore.data?.kiosk_preloader" loop autoplay muted playsinline crossorigin="anonymous">
			<source :src="themeStore.data?.kiosk_preloader" type="video/webm">
		</video>
		<div class="start_img_black" :class="{ darken: themeStore.data?.kiosk_preloader_darken }"></div>
		<div class="modal_qr">
			<div class="modal_qr_content">
				<div class="wrapper_icon_qr">
					<img :src="QrScanner" width="100" height="100" alt="">
				</div>
				<h1>{{ t(`cart-complete.qr-code.title`) }}</h1>
				<p class="total_order_price">
					<AnimNum :price="price" :symbol="storeMain.config_lib.currency_symbol" />
				</p>

				<div class="ttl_wrapper">
					<div class="ttl_head">
						<span>{{ t(`cart-complete.qr-code.ttl`) }}</span>
						<b>{{ formattedTime }}</b>
					</div>
					<div class="ttl_bar">
						<div class="ttl_fill" :style="{ width: `${ttlPercent}%` }"></div>
					</div>
				</div>

				<div class="hint hint-anim">
					<div class="steps" aria-hidden="true">
						<div class="step is-active" data-i="0">
							<div class="ico">
								<QrPhone />
							</div>
							<div class="txt">{{ t(`cart-complete.qr-code.step-1`) }}</div>
						</div>

						<div class="sep"></div>

						<div class="step" data-i="1">
							<div class="ico">
								<QRScannerTutor />
							</div>
							<span class="txt">{{ t(`cart-complete.qr-code.step-2`) }}</span>
						</div>

						<span class="sep">→</span>

						<div class="step" data-i="2">
							<div class="ico">
								<img :src="QRCheckTutor" width="30" height="30" alt="">
							</div>
							<span class="txt">{{ t(`cart-complete.qr-code.step-3`) }}</span>
						</div>
					</div>
				</div>

				<div class="qr_border">
					<QrcodeVue :value="link" :size="200" level="Q" render-as="svg" :image-settings="imageSettings" />
				</div>

				<ButtonDefault :textButtonDefault="t(`cart-complete.qr-code.button.cancel-payment`)" :onClick="resetOrder" class="cancel_order_btn" />
			</div>
		</div>
	</div>
</template>

<style lang="scss" scoped>
  @use './completedQRCode';
</style>
